/**
 * Use this file for JavaScript code that you want to run in the front-end 
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any 
 * JavaScript running in the front-end, then you should delete this file and remove 
 * the `viewScript` property from `block.json`. 
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

/* eslint-disable no-console */
// console.log("Hello World! (from ultime-blocks-lights-psychedelic block)");
/* eslint-enable no-console */

(function () {
    function init() {
        const canvas = document.getElementById('pg-canvas');
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const wrap = document.getElementById('pg-canvas-wrap');
        const styleGrid = document.getElementById('pg-style-grid');
        const overlay = document.getElementById('pg-start-overlay');
        const toggleBtn = document.getElementById('pg-toggle');
        const snapshotBtn = document.getElementById('pg-snapshot');
        const resetBtn = document.getElementById('pg-reset');
        const launchBtn = document.getElementById('pg-launch');
        const fsBtn = document.getElementById('pg-fs-btn');
        const fsExpand = document.getElementById('pg-fs-expand');
        const fsCompress = document.getElementById('pg-fs-compress');
        const speedEl = document.getElementById('pg-speed');
        const zoomEl = document.getElementById('pg-zoom');
        const intensityEl = document.getElementById('pg-intensity');
        const paletteEl = document.getElementById('pg-palette');
        const resEl = document.getElementById('pg-resolution');

        let running = false, rafId = null, animT = 0, lastTs = null;
        let cw = 0, ch = 0;
        let activeStyle = 'plasma';

        // ── Palettes ──────────────────────────────────────────────────────
        function getColor(v, palette) {
            v = Math.max(0, Math.min(1, v));
            const tau = Math.PI * 2;
            let r, g, b;
            switch (palette) {
                case 'rainbow':
                    r = Math.sin(tau * v) * 127 + 128;
                    g = Math.sin(tau * v + tau / 3) * 127 + 128;
                    b = Math.sin(tau * v + 2 * tau / 3) * 127 + 128; break;
                case 'fire':
                    r = Math.min(255, v * 3 * 255);
                    g = Math.max(0, Math.min(255, (v - .33) * 3 * 255));
                    b = Math.max(0, Math.min(255, (v - .66) * 3 * 255)); break;
                case 'neon':
                    r = Math.sin(tau * v * 1.5) * 127 + 128;
                    g = Math.sin(tau * v * 1.5 + 2.1) * 127 + 128;
                    b = 255; break;
                case 'acid':
                    r = Math.abs(Math.sin(tau * v * 2.3)) * 255;
                    g = Math.abs(Math.sin(tau * v * 3.1 + 1.5)) * 255;
                    b = Math.abs(Math.sin(tau * v * 1.7 + .8)) * 255; break;
                case 'ocean':
                    r = v * 30;
                    g = Math.sin(tau * v * .8 + 1) * 80 + 100;
                    b = Math.sin(tau * v * .5) * 100 + 155; break;
                case 'sunset':
                    r = Math.min(255, v * 2 * 255);
                    g = Math.abs(Math.sin(tau * v * .7)) * 150;
                    b = Math.max(0, (1 - v * 1.5)) * 200; break;
                case 'forest':
                    r = Math.abs(Math.sin(tau * v * .5)) * 100;
                    g = Math.sin(tau * v * .6 + .5) * 120 + 135;
                    b = Math.abs(Math.sin(tau * v * .4)) * 80; break;
                case 'candy':
                    r = Math.sin(tau * v) * 80 + 175;
                    g = Math.sin(tau * v + tau / 4) * 80 + 175;
                    b = Math.sin(tau * v + tau / 2) * 80 + 175; break;
                case 'mono_cyan': r = 0; g = v * 255; b = v * 255; break;
                case 'mono_magenta': r = v * 255; g = 0; b = v * 255; break;
                case 'mono_gold': r = v * 255; g = v * 200; b = 0; break;
                default: r = g = b = v * 255;
            }
            return [Math.round(r), Math.round(g), Math.round(b)];
        }

        // ── Perlin noise ──────────────────────────────────────────────────
        const P = (() => {
            const p = Array.from({ length: 256 }, (_, i) => i);
            for (let i = 255; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[p[i], p[j]] = [p[j], p[i]]; }
            return [...p, ...p];
        })();
        const fade = t => t * t * t * (t * (t * 6 - 15) + 10);
        const lrp = (a, b, t) => a + t * (b - a);
        const grad = (h, x, y) => { const d = h & 3; return d === 0 ? x + y : d === 1 ? -x + y : d === 2 ? x - y : -x - y; };
        function noise(x, y) {
            const X = Math.floor(x) & 255, Y = Math.floor(y) & 255;
            x -= Math.floor(x); y -= Math.floor(y);
            const u = fade(x), v = fade(y), a = P[X] + Y, b = P[X + 1] + Y;
            return lrp(lrp(grad(P[a], x, y), grad(P[b], x - 1, y), u), lrp(grad(P[a + 1], x, y - 1), grad(P[b + 1], x - 1, y - 1), u), v);
        }
        function fbm(x, y, oct = 5) {
            let v = 0, amp = .5, f = 1, m = 0;
            for (let i = 0; i < oct; i++) { v += noise(x * f, y * f) * amp; m += amp; f *= 2; amp *= .5; }
            return v / m;
        }

        // ── Renderers ─────────────────────────────────────────────────────
        const R = {
            plasma(d, w, h, t, sp, zm, it, pal) {
                const sc = zm * .008, ts = t * sp;
                for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
                    const nx = x * sc, ny = y * sc;
                    const v = (Math.sin(nx + ts) + Math.sin(ny + ts * .7) + Math.sin((nx + ny) * .5 + ts * .5) + Math.sin(Math.sqrt(nx * nx + ny * ny) * it + ts)) / 4 * .5 + .5;
                    const [r, g, b] = getColor(v, pal), i = (y * w + x) * 4;
                    d[i] = r; d[i + 1] = g; d[i + 2] = b; d[i + 3] = 255;
                }
            },
            kaleidoscope(d, w, h, t, sp, zm, it, pal) {
                const cx = w / 2, cy = h / 2, ts = t * sp, seg = Math.round(8 * it), sector = Math.PI * 2 / seg;
                for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
                    const dx = x - cx, dy = y - cy, r = Math.sqrt(dx * dx + dy * dy);
                    let a = ((Math.atan2(dy, dx) + ts * .15) % sector + sector) % sector;
                    if (a > sector / 2) a = sector - a;
                    const v = (fbm(Math.cos(a) * r * zm * .006 + ts * .2, Math.sin(a) * r * zm * .006, 4) + 1) * .5;
                    const [r2, g, b] = getColor(v, pal), i = (y * w + x) * 4;
                    d[i] = r2; d[i + 1] = g; d[i + 2] = b; d[i + 3] = 255;
                }
            },
            mandala(d, w, h, t, sp, zm, it, pal) {
                const cx = w / 2, cy = h / 2, ts = t * sp, sym = Math.round(6 * it);
                for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
                    const dx = x - cx, dy = y - cy;
                    const r = Math.sqrt(dx * dx + dy * dy) / (Math.min(w, h) * .5) * zm;
                    const a = Math.atan2(dy, dx), sa = (a % (Math.PI * 2 / sym)) * sym;
                    const v = (Math.sin(r * 6 - ts) + Math.cos(sa * 2 + ts * .5) + Math.sin((r + sa) * it)) / 3 * .5 + .5;
                    const [r2, g, b] = getColor(v, pal), i = (y * w + x) * 4;
                    d[i] = r2; d[i + 1] = g; d[i + 2] = b; d[i + 3] = 255;
                }
            },
            waves(d, w, h, t, sp, zm, it, pal) {
                const ts = t * sp, sc = zm * .01;
                for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
                    const nx = x * sc, ny = y * sc;
                    const v = (Math.sin(nx * it + ts) * Math.sin(ny * it * .8 + ts * 1.3) + Math.sin(nx * ny * .1 * it + ts * .6)) / 2 * .5 + .5;
                    const [r, g, b] = getColor(v, pal), i = (y * w + x) * 4;
                    d[i] = r; d[i + 1] = g; d[i + 2] = b; d[i + 3] = 255;
                }
            },
            perlinFlow(d, w, h, t, sp, zm, it, pal) {
                const ts = t * sp * .3, sc = zm * .004;
                for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
                    const nx = x * sc, ny = y * sc;
                    const v = (fbm(nx + ts, ny + ts * .7, 5) * it + 1) * .5;
                    const combined = ((v + fbm(nx * .5 + ts * .4, ny * .5, 4) * it) / 2) % 1;
                    const [r, g, b] = getColor(combined, pal), i = (y * w + x) * 4;
                    d[i] = r; d[i + 1] = g; d[i + 2] = b; d[i + 3] = 255;
                }
            },
            voronoi(d, w, h, t, sp, zm, it, pal) {
                const ts = t * sp, N = Math.round(12 * it);
                const pts = Array.from({ length: N }, (_, k) => ({
                    x: ((k * 7919) % 997 / 997 * .8 + .1) * w, y: ((k * 6271) % 887 / 887 * .8 + .1) * h,
                    vx: Math.sin(k * 1.3 + ts) * w * .003 * zm, vy: Math.cos(k * .9 + ts) * h * .003 * zm
                }));
                pts.forEach(p => { p.x = (p.x + p.vx * 30 + w) % w; p.y = (p.y + p.vy * 30 + h) % h; });
                for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
                    let d1 = Infinity, d2 = Infinity, ci = 0;
                    for (let k = 0; k < N; k++) { const dx = x - pts[k].x, dy = y - pts[k].y, dist = dx * dx + dy * dy; if (dist < d1) { d2 = d1; d1 = dist; ci = k; } else if (dist < d2) d2 = dist; }
                    const v = ((Math.sqrt(d2) - Math.sqrt(d1)) / Math.min(w, h) * zm * 3 + ci / N) % 1;
                    const [r, g, b] = getColor(Math.abs(v), pal), i = (y * w + x) * 4;
                    d[i] = r; d[i + 1] = g; d[i + 2] = b; d[i + 3] = 255;
                }
            },
            interference(d, w, h, t, sp, zm, it, pal) {
                const ts = t * sp, freq = zm * .04 * it;
                const src = [{ x: w * .25, y: h * .35 }, { x: w * .75, y: h * .35 }, { x: w * .5, y: h * .75 }, { x: w * .5, y: h * .15 }];
                for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
                    let sum = 0; src.forEach((s, si) => { const dx = x - s.x, dy = y - s.y; sum += Math.sin(Math.sqrt(dx * dx + dy * dy) * freq - ts * (1 + si * .2)); });
                    const [r, g, b] = getColor((sum / src.length * .5 + .5) % 1, pal), i = (y * w + x) * 4;
                    d[i] = r; d[i + 1] = g; d[i + 2] = b; d[i + 3] = 255;
                }
            },
            moire(d, w, h, t, sp, zm, it, pal) {
                const ts = t * sp, freq = zm * .06 * it;
                for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
                    const v = (Math.sin(x * freq + ts) * Math.sin(y * freq * 1.01 + ts * .99) + Math.sin((x + y) * freq * .7 + ts * 1.3)) / 2 * .5 + .5;
                    const [r, g, b] = getColor(v, pal), i = (y * w + x) * 4;
                    d[i] = r; d[i + 1] = g; d[i + 2] = b; d[i + 3] = 255;
                }
            },
        };

        // ── Thumbnails + click — cards already in static HTML ─────────────
        styleGrid.querySelectorAll('.pg-style-card').forEach(card => {
            const id = card.dataset.id;
            const cv = card.querySelector('.pg-style-thumb');
            const sctx = cv.getContext('2d');
            const id2 = sctx.createImageData(120, 50);
            try { (R[id] || R.plasma)(id2.data, 120, 50, 1.5, 1, 1, 1, 'rainbow'); } catch (e) { }
            sctx.putImageData(id2, 0, 0);

            card.addEventListener('click', () => {
                activeStyle = id;
                styleGrid.querySelectorAll('.pg-style-card').forEach(c => c.classList.remove('active'));
                card.classList.add('active');
                if (!running) start(); else { animT = 0; lastTs = null; }
            });
        });

        // ── Resize with retry ─────────────────────────────────────────────
        function resize() {
            cw = wrap.clientWidth; ch = wrap.clientHeight;
            if (!cw || !ch) { setTimeout(resize, 50); return; }
            const res = parseFloat(resEl.value);
            const dpr = window.devicePixelRatio || 1;
            canvas.width = Math.round(cw * dpr * res);
            canvas.height = Math.round(ch * dpr * res);
        }

        new ResizeObserver(() => resize()).observe(wrap);

        // ── RAF loop ──────────────────────────────────────────────────────
        function frame(ts) {
            if (!running) return;
            if (lastTs === null) lastTs = ts;
            animT += Math.min((ts - lastTs) / 1000, 0.05); lastTs = ts;
            const W = canvas.width, H = canvas.height;
            const imgData = ctx.createImageData(W, H);
            (R[activeStyle] || R.plasma)(imgData.data, W, H, animT, parseFloat(speedEl.value), parseFloat(zoomEl.value), parseFloat(intensityEl.value), paletteEl.value);
            ctx.putImageData(imgData, 0, 0);
            rafId = requestAnimationFrame(frame);
        }

        // ── Controls ──────────────────────────────────────────────────────
        function start() {
            if (running) return;
            running = true; lastTs = null;
            overlay.classList.add('pg-hidden');
            toggleBtn.textContent = '⏸ Pause';
            rafId = requestAnimationFrame(frame);
        }
        function pause() {
            running = false; if (rafId) cancelAnimationFrame(rafId);
            rafId = null; lastTs = null; toggleBtn.textContent = '▶ Resume';
        }
        function reset() {
            animT = 0; lastTs = null;
            if (!running) start();
        }

        toggleBtn.addEventListener('click', () => running ? pause() : start());
        launchBtn.addEventListener('click', start);
        resetBtn.addEventListener('click', reset);
        resEl.addEventListener('change', resize);

        snapshotBtn.addEventListener('click', () => {
            const a = document.createElement('a');
            a.download = `psychedelic-${activeStyle}-${Date.now()}.png`;
            a.href = canvas.toDataURL('image/png');
            a.click();
        });

        speedEl.addEventListener('input', () => document.getElementById('pg-speed-val').textContent = speedEl.value);
        zoomEl.addEventListener('input', () => document.getElementById('pg-zoom-val').textContent = zoomEl.value);
        intensityEl.addEventListener('input', () => document.getElementById('pg-intensity-val').textContent = intensityEl.value);

        fsBtn.addEventListener('click', () => {
            if (!document.fullscreenElement) wrap.requestFullscreen().catch(() => { });
            else document.exitFullscreen().catch(() => { });
        });

        document.addEventListener('fullscreenchange', () => {
            const fs = !!document.fullscreenElement;
            fsExpand.style.display = fs ? 'none' : 'block';
            fsCompress.style.display = fs ? 'block' : 'none';
            fsBtn.setAttribute('aria-label', fs ? 'Exit full screen' : 'Full screen');
            wrap.classList.toggle('pg-fullscreen', fs);
            setTimeout(resize, 30);
        });

        // ── Init ──────────────────────────────────────────────────────────
        resize();
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
}());